import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Save, Plus, Trash2, Loader2, ToggleLeft, ToggleRight, Pencil, X,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import Backdrop from "@/theme/Backdrop";
import Button from "@/theme/Button";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useContent } from "@/lib/content";
import { LOGO_CIRCLE } from "@/theme/tokens";
import AnalyticsOverview from "@/pages/admin/analytics/AnalyticsOverview";
import HeatmapView from "@/pages/admin/analytics/HeatmapView";
import VisitorsView from "@/pages/admin/analytics/VisitorsView";
import SlideDeckView from "@/pages/admin/analytics/SlideDeckView";
import { DaysSelector } from "@/pages/admin/analytics/shared";

const TABS = [
  { id: "site", label: "Site Content" },
  { id: "clientwork", label: "Client Work" },
  { id: "games", label: "Our Games" },
  { id: "assets", label: "Assets" },
  { id: "testimonials", label: "Testimonials" },
  { id: "clients", label: "Clients" },
  { id: "contact-requests", label: "Inquiries" },
  { id: "analytics", label: "Analytics" },
  { id: "heatmaps", label: "Heatmaps" },
  { id: "visitors", label: "Visitors" },
  { id: "slidedeck", label: "Slide Deck" },
  { id: "settings", label: "Settings" },
];

const ANALYTICS_TABS = new Set(["analytics", "heatmaps", "visitors", "slidedeck"]);

const SECTION_FIELDS = {
  testimonials: [
    { k: "text", label: "Quote", type: "textarea", required: true },
    { k: "clientName", label: "Client Name", required: true },
    { k: "clientTitle", label: "Title / Company" },
    { k: "clientImage", label: "Image URL" },
  ],
  clientwork: [
    { k: "title", label: "Game Title", required: true },
    { k: "icon", label: "Icon URL" },
    { k: "iconPaddingLeft", label: "Image Left Offset (px)" },
    { k: "iconPaddingTop", label: "Image Top Offset (px)" },
    { k: "playStoreLink", label: "Google Play URL" },
    { k: "appStoreLink", label: "App Store URL" },
    { k: "steamLink", label: "Steam URL" },
  ],
  games: [
    { k: "title", label: "Game Title", required: true },
    { k: "icon", label: "Icon URL" },
    { k: "iconPaddingLeft", label: "Image Left Offset (px)" },
    { k: "iconPaddingTop", label: "Image Top Offset (px)" },
    { k: "playStoreLink", label: "Google Play URL" },
    { k: "appStoreLink", label: "App Store URL" },
    { k: "steamLink", label: "Steam URL" },
  ],
  clients: [
    { k: "name", label: "Company Name", required: true },
    { k: "logo", label: "Logo URL" },
  ],
  assets: [
    { k: "title", label: "Asset Title", required: true },
    { k: "icon", label: "Image URL" },
    { k: "playStoreLink", label: "Asset Store URL" },
  ],
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const content = useContent();
  const nav = useNavigate();
  const [tab, setTab] = useState("site");
  const [days, setDays] = useState(30);

  const handleLogout = () => {
    logout();
    nav("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#060606", color: "#fff" }}>
      <Backdrop />
      <Toaster theme="dark" position="bottom-right" />

      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: "rgba(6,6,6,0.85)",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_CIRCLE} alt="" className="w-9 h-9 rounded-full" style={{ background: "#fff" }} />
            <div>
              <div
                className="font-black text-xs tracking-[0.3em] uppercase"
                style={{ fontFamily: "'Azeret Mono', monospace" }}
              >
                Admin
              </div>
              <div className="text-[10px] tracking-[0.22em] text-white/50">{user?.email}</div>
            </div>
          </div>
          <Button data-testid="admin-logout" size="sm" variant="outline" onClick={handleLogout}>
            <LogOut size={14} strokeWidth={3} /> Sign out
          </Button>
        </div>
        <nav className="max-w-7xl mx-auto px-5 md:px-8 flex gap-2 overflow-x-auto pb-2 -mt-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="px-4 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-bold whitespace-nowrap transition-colors"
              style={{
                background: tab === t.id ? "#c8ff2a" : "rgba(255,255,255,0.06)",
                color: tab === t.id ? "#060606" : "rgba(255,255,255,0.75)",
                border: "1px solid " + (tab === t.id ? "#c8ff2a" : "rgba(255,255,255,0.10)"),
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="flex-1 relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 py-10">
        {ANALYTICS_TABS.has(tab) && (
          <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
            <h2
              className="font-black uppercase text-base md:text-lg"
              style={{ fontFamily: "'Azeret Mono', monospace", letterSpacing: "-0.03em" }}
            >
              {TABS.find((t) => t.id === tab)?.label}
            </h2>
            <DaysSelector days={days} onChange={setDays} />
          </div>
        )}
        {tab === "site" && <SiteContentTab content={content} />}
        {tab === "clientwork" && <ItemsTab section="clientwork" content={content} />}
        {tab === "games" && <ItemsTab section="games" content={content} />}
        {tab === "assets" && <ItemsTab section="assets" content={content} />}
        {tab === "testimonials" && <ItemsTab section="testimonials" content={content} />}
        {tab === "clients" && <ItemsTab section="clients" content={content} />}
        {tab === "contact-requests" && <InquiriesTab />}
        {tab === "analytics" && <AnalyticsOverview days={days} />}
        {tab === "heatmaps" && <HeatmapView days={days} />}
        {tab === "visitors" && <VisitorsView days={days} />}
        {tab === "slidedeck" && <SlideDeckView days={days} />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
};

/* ---------- Site Content Tab ---------- */
const SiteContentTab = ({ content }) => {
  const [form, setForm] = useState(content.siteContent);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(content.siteContent);
  }, [content.siteContent]);

  const update = (path) => (val) => {
    setForm((f) => {
      const next = JSON.parse(JSON.stringify(f));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/site", form);
      await content.refresh();
      toast.success("Site content saved.");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-site-tab">
      <Card title="Hero">
        <Grid>
          <TextField label="Tagline" value={form.hero.tagline} onChange={update("hero.tagline")} testid="site-hero-tagline" />
          <TextField label="CTA Primary" value={form.hero.ctaPrimary} onChange={update("hero.ctaPrimary")} testid="site-hero-cta1" />
        </Grid>
        <TextField label="Title (HTML, use <br/> for line breaks)" value={form.hero.title} onChange={update("hero.title")} testid="site-hero-title" />
        <div className="mt-4 mb-2">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-white/60 mb-3">Line Colors</label>
          <div className="flex flex-wrap gap-4">
            {(form.hero.lineColors || ["#c8ff2a", "#ff2a8f", "#2ad6ff", "#ffffff"]).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-white/50">Line {i + 1}</span>
                <input
                  type="color"
                  value={c}
                  data-testid={`site-hero-line-color-${i}`}
                  onChange={(e) => {
                    const colors = [...(form.hero.lineColors || ["#c8ff2a", "#ff2a8f", "#2ad6ff", "#ffffff"])];
                    colors[i] = e.target.value;
                    update("hero.lineColors")(colors);
                  }}
                  className="w-10 h-10 rounded cursor-pointer border border-white/20 bg-transparent"
                />
                <span className="text-[10px] text-white/40 font-mono">{c}</span>
              </div>
            ))}
          </div>
        </div>
        <TextField label="Subtitle" value={form.hero.subtitle} onChange={update("hero.subtitle")} multiline testid="site-hero-subtitle" />
        <TextField label="CTA Secondary" value={form.hero.ctaSecondary} onChange={update("hero.ctaSecondary")} testid="site-hero-cta2" />
      </Card>

      <Card title="Services">
        <Grid>
          <TextField label="Section Label" value={form.services.label} onChange={update("services.label")} />
          <TextField label="Section Title (HTML)" value={form.services.title} onChange={update("services.title")} />
        </Grid>
        <TextField label="Section Subtitle" value={form.services.subtitle} onChange={update("services.subtitle")} multiline />
        <div className="space-y-3 mt-4">
          {(form.services.items || []).map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 rounded-xl border border-white/10">
              <TextField label="Icon" value={s.icon} onChange={(v) => update(`services.items.${i}.icon`)(v)} className="md:col-span-2" />
              <TextField label="Title" value={s.title} onChange={(v) => update(`services.items.${i}.title`)(v)} className="md:col-span-3" />
              <TextField label="Description" value={s.desc} onChange={(v) => update(`services.items.${i}.desc`)(v)} multiline className="md:col-span-7" />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Contact">
        <TextField label="Address (HTML)" value={form.contact.address} onChange={update("contact.address")} multiline />
        <Grid>
          <TextField label="Phone" value={form.contact.phone} onChange={update("contact.phone")} />
          <TextField label="Email" value={form.contact.email} onChange={update("contact.email")} />
        </Grid>
      </Card>

      <Card title="Footer">
        <TextField label="Footer Text (year auto-prepended)" value={form.footer.text} onChange={update("footer.text")} />
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="admin-save-site">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={3} />} Save site content
        </Button>
      </div>
    </div>
  );
};

/* ---------- Items Tab ---------- */
const ItemsTab = ({ section, content }) => {
  const fields = SECTION_FIELDS[section] || [];
  const sectionData = content.sections[section] || {};
  const [editing, setEditing] = useState(null); // item being edited or `new`
  const [saving, setSaving] = useState(false);

  const toggleSection = async () => {
    try {
      await api.put(`/content/section/${section}`, { enabled: !sectionData.enabled });
      await content.refresh();
      toast.success(`${section} ${!sectionData.enabled ? "shown" : "hidden"}.`);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail, "Update failed."));
    }
  };

  const saveItem = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: editing.enabled ?? true,
        order: editing.order ?? 0,
        data: editing.data || {},
      };
      if (editing._mode === "new") {
        await api.post(`/items/${section}`, payload);
      } else {
        await api.put(`/items/${section}/${editing.id}`, payload);
      }
      await content.refresh();
      toast.success("Saved.");
      setEditing(null);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/items/${section}/${id}`);
      await content.refresh();
      toast.success("Deleted.");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail, "Delete failed."));
    }
  };

  const toggleItem = async (it) => {
    try {
      await api.put(`/items/${section}/${it.id}`, {
        enabled: !it.enabled,
        order: it.order || 0,
        data: stripMeta(it),
      });
      await content.refresh();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail, "Toggle failed."));
    }
  };

  return (
    <div className="space-y-6" data-testid={`admin-items-${section}`}>
      <Card
        title={null}
        topAction={
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={toggleSection}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-bold text-white/75"
              data-testid={`section-visible-${section}`}
            >
              {sectionData.enabled ? <ToggleRight size={22} className="text-[#c8ff2a]" /> : <ToggleLeft size={22} className="text-white/40" />}
              Section visible
            </button>
            <Button
              size="sm"
              onClick={() => setEditing({ _mode: "new", enabled: true, order: (sectionData.items?.length || 0), data: {} })}
              data-testid={`admin-add-${section}`}
            >
              <Plus size={14} strokeWidth={3} /> Add
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(sectionData.items || []).map((it, idx) => (
            <article
              key={it.id}
              className="p-4 rounded-2xl border border-white/10 flex gap-4 items-start"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              {(it.icon || it.logo || it.clientImage) && (
                <img
                  src={it.icon || it.logo || it.clientImage}
                  alt=""
                  className="w-14 h-14 rounded-xl object-cover bg-white/10 shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white truncate">
                  {it.title || it.name || it.clientName || "Untitled"}
                </div>
                <div className="text-xs text-white/55 truncate mt-0.5">
                  {it.clientTitle || it.text || it.playStoreLink || ""}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => toggleItem(it)}
                    className="text-[10px] tracking-[0.22em] uppercase font-bold inline-flex items-center gap-1"
                    style={{ color: it.enabled ? "#c8ff2a" : "rgba(255,255,255,0.45)" }}
                  >
                    {it.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    {it.enabled ? "Visible" : "Hidden"}
                  </button>
                  <button
                    onClick={() => setEditing({ _mode: "edit", id: it.id, enabled: it.enabled, order: it.order ?? idx, data: stripMeta(it) })}
                    className="ml-auto p-1.5 rounded-lg border border-white/10 hover:border-[#c8ff2a]/50 text-white/65 hover:text-[#c8ff2a]"
                    data-testid={`edit-${section}-${idx}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => removeItem(it.id)}
                    className="p-1.5 rounded-lg border border-white/10 hover:border-[#ff2a8f]/50 text-white/65 hover:text-[#ff2a8f]"
                    data-testid={`delete-${section}-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </article>
          ))}
          {(!sectionData.items || sectionData.items.length === 0) && (
            <div className="md:col-span-2 text-center text-white/45 py-8 border border-dashed border-white/10 rounded-2xl">
              No items yet. Click <strong>Add</strong> to create one.
            </div>
          )}
        </div>
      </Card>

      {/* Modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.6)" }}
          data-testid="admin-edit-modal"
        >
          <div
            className="w-full max-w-xl p-6 rounded-2xl space-y-4"
            style={{
              background: "#0d0d0d",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <div className="flex items-center justify-between">
              <h3
                className="font-black uppercase text-lg text-white"
                style={{ fontFamily: "'Azeret Mono', monospace", letterSpacing: "-0.03em" }}
              >
                {editing._mode === "new" ? "Add" : "Edit"} item
              </h3>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white">
                <X size={18} />
              </button>
            </div>
            {fields.map((f) => (
              <TextField
                key={f.k}
                label={f.label + (f.required ? " *" : "")}
                value={editing.data?.[f.k] || ""}
                onChange={(v) =>
                  setEditing((e) => ({ ...e, data: { ...e.data, [f.k]: v } }))
                }
                multiline={f.type === "textarea"}
              />
            ))}
            <div className="flex items-center gap-3 pt-2">
              <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/70 font-bold">
                <input
                  type="checkbox"
                  checked={editing.enabled !== false}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  className="accent-[#c8ff2a]"
                />
                Visible
              </label>
              <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/70 font-bold">
                Order
                <input
                  type="number"
                  value={editing.order ?? 0}
                  onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })}
                  className="w-16 px-2 py-1 rounded bg-transparent border border-white/15 text-white"
                />
              </label>
              <Button onClick={saveItem} disabled={saving} className="ml-auto" data-testid="admin-modal-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={3} />} Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const stripMeta = (it) => {
  const { id, enabled, order, ...rest } = it;
  return rest;
};

/* ---------- Inquiries Tab ---------- */
const InquiriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contact");
        setItems(data);
      } catch (e) {
        toast.error(formatError(e?.response?.data?.detail, "Failed to load inquiries."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  if (loading) return <div className="text-white/60 text-sm">Loading…</div>;
  return (
    <Card title={`Contact Inquiries (${items.length})`}>
      {items.length === 0 ? (
        <div className="text-white/45 text-sm text-center py-6">No inquiries yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="p-4 rounded-xl border border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-bold text-white">{it.name}</div>
                  <div className="text-xs text-white/55">
                    {it.email} {it.phone ? `· ${it.phone}` : ""}
                  </div>
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">
                  {new Date(it.created_at).toLocaleString()}
                </div>
              </div>
              <p className="mt-2 text-sm text-white/80 whitespace-pre-wrap">{it.message}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

/* ---------- Settings Tab ---------- */
const SettingsTab = () => {
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail, "Password change failed."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card title="Change Admin Password">
      <form onSubmit={save} className="space-y-4 max-w-md" data-testid="admin-password-form">
        <TextField type="password" label="Current password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} />
        <TextField type="password" label="New password" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} />
        <TextField type="password" label="Confirm new password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} />
        <Button type="submit" disabled={saving} data-testid="admin-password-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={3} />} Update password
        </Button>
      </form>
    </Card>
  );
};

/* ---------- Shared form components ---------- */
const Card = ({ title, topAction, children }) => (
  <section
    className="p-6 md:p-7 rounded-2xl space-y-4"
    style={{
      background: "rgba(13,13,13,0.7)",
      border: "1px solid rgba(255,255,255,0.10)",
      backdropFilter: "blur(8px)",
    }}
  >
    {(title || topAction) && (
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {title && (
          <h2
            className="font-black uppercase text-base md:text-lg"
            style={{ fontFamily: "'Azeret Mono', monospace", letterSpacing: "-0.03em" }}
          >
            {title}
          </h2>
        )}
        {topAction}
      </div>
    )}
    {children}
  </section>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const TextField = ({ label, value, onChange, multiline, className = "", type = "text", testid }) => (
  <div className={className}>
    <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold block mb-2">
      {label}
    </label>
    {multiline ? (
      <textarea
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-lg bg-transparent border border-white/15 text-white focus:border-[#c8ff2a] focus:outline-none focus:ring-2 focus:ring-[#c8ff2a]/30 transition-colors resize-y text-sm"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-transparent border border-white/15 text-white focus:border-[#c8ff2a] focus:outline-none focus:ring-2 focus:ring-[#c8ff2a]/30 transition-colors text-sm"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      />
    )}
  </div>
);

export default DashboardPage;
